import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { LayoutShell } from '../../shared/layouts';
import { adminService } from '../../shared/services/api/adminService';

const cardSx = {
  borderRadius: '16px',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  height: '100%',
};

export const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  const axisStyle = { fill: theme.palette.text.secondary, fontSize: theme.typography.body2.fontSize };
  const gridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';

  const { data: dashboard, isFetching } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => adminService.getDashboard(),
  });

  const formatBRL = (value: number) =>
    `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (isFetching) {
    return (
      <LayoutShell>
        <Box display='flex' justifyContent='center' py={8}>
          <CircularProgress />
        </Box>
      </LayoutShell>
    );
  }

  if (!dashboard) {
    return (
      <LayoutShell>
        <Typography color='text.secondary'>Nenhum dado disponível.</Typography>
      </LayoutShell>
    );
  }

  return (
    <LayoutShell>
      <Box display='flex' flexDirection='column' gap={3}>
        <Typography variant='h5' fontWeight={600}>
          Dashboard do Sistema
        </Typography>

        {/* KPI cards */}
        <Box display='flex' gap={2} flexWrap='wrap'>
          <KpiCard label='Total de Usuários' value={dashboard.totalUsers} color='primary.main' />
          <KpiCard label='Novos Usuários (mês)' value={dashboard.newUsersThisMonth} color='success.main' />
          <KpiCard label='Total de Estabelecimentos' value={dashboard.totalBusinesses} color='primary.main' />
          <KpiCard label='Estabelecimentos Ativos' value={dashboard.activeBusinesses} color='success.main' />
          <KpiCard label='Novos Estab. (mês)' value={dashboard.newBusinessesThisMonth} color='warning.main' />
          <KpiCard label='Agendamentos (mês)' value={dashboard.appointmentsThisMonth} color='info.main' />
          <KpiCard label='Receita Bruta (mês)' value={formatBRL(dashboard.revenueThisMonth)} color='warning.main' />
        </Box>

        {/* Charts row 1 */}
        <Box display='flex' gap={2} flexWrap='wrap'>
          <ChartCard title='Novos Usuários por Mês' flex={1}>
            <ResponsiveContainer width='100%' height={220}>
              <AreaChart data={dashboard.usersByMonth} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id='gradUsers' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor={primaryColor} stopOpacity={0.3} />
                    <stop offset='95%' stopColor={primaryColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray='3 3' stroke={gridColor} />
                <XAxis dataKey='label' tick={axisStyle} interval='preserveStartEnd' />
                <YAxis tick={axisStyle} allowDecimals={false} />
                <Tooltip />
                <Area type='monotone' dataKey='value' stroke={primaryColor} strokeWidth={2} fill='url(#gradUsers)' />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title='Novos Estabelecimentos por Mês' flex={1}>
            <ResponsiveContainer width='100%' height={220}>
              <AreaChart data={dashboard.businessesByMonth} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id='gradBiz' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor={theme.palette.success.main} stopOpacity={0.3} />
                    <stop offset='95%' stopColor={theme.palette.success.main} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray='3 3' stroke={gridColor} />
                <XAxis dataKey='label' tick={axisStyle} interval='preserveStartEnd' />
                <YAxis tick={axisStyle} allowDecimals={false} />
                <Tooltip />
                <Area type='monotone' dataKey='value' stroke={theme.palette.success.main} strokeWidth={2} fill='url(#gradBiz)' />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Box>

        {/* Charts row 2 */}
        <Box display='flex' gap={2} flexWrap='wrap'>
          <ChartCard title='Agendamentos por Mês' flex={1}>
            <ResponsiveContainer width='100%' height={220}>
              <BarChart data={dashboard.appointmentsByMonth} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray='3 3' stroke={gridColor} />
                <XAxis dataKey='label' tick={axisStyle} interval='preserveStartEnd' />
                <YAxis tick={axisStyle} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey='value' fill={theme.palette.info.main} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title='Receita por Mês' flex={1}>
            <ResponsiveContainer width='100%' height={220}>
              <BarChart data={dashboard.revenueByMonth} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray='3 3' stroke={gridColor} />
                <XAxis dataKey='label' tick={axisStyle} interval='preserveStartEnd' />
                <YAxis tick={axisStyle} tickFormatter={v => `R$${v}`} />
                <Tooltip formatter={(v: unknown) => [formatBRL(Number(v)), 'Receita']} />
                <Bar dataKey='value' fill={theme.palette.warning.main} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Box>
      </Box>
    </LayoutShell>
  );
};

/* ── Sub-components ── */

interface KpiCardProps {
  label: string;
  value: string | number;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, color }) => (
  <Card sx={{ ...cardSx, minWidth: 150, flex: 1 }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='h6' fontWeight={700} color={color}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

interface ChartCardProps {
  title: string;
  flex?: number;
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, flex, children }) => (
  <Card sx={{ ...cardSx, flex, minWidth: 280 }}>
    <CardContent>
      <Typography variant='subtitle1' fontWeight={700} mb={2}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);
